import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contextProviders/AuthProvider';
import { useUserProfile } from '../contextProviders/UserProfileProvider';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { PENDING_AGE_VERIFICATION_KEY } from '../services/AgeVerificationService';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface ProtectedRouteProps {
  children: React.ReactElement;
  // Also requires the signed-in user's AppRole claim to be "Admin" (see
  // AuthProvider). Use for admin-only routes.
  requireAdmin?: boolean;
  // Same idea, but also lets "Moderator" through -- use for the admin panel
  // itself (/admin), where Moderators get in but with fewer permissions
  // than Admin once inside (enforced per-section, e.g. "Roles y permisos").
  requireAdminOrModerator?: boolean;
}

/**
 * ProtectedRoute - Protege rutas que requieren autenticación
 * 
 * Flujo:
 * 1. Si loading → Muestra spinner (verificando autenticación)
 * 2. Si NO autenticado → Guarda la URL destino y redirect a /login
 * 3. Si autenticado → Muestra el componente hijo
 * 
 * Uso:
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <Dashboard />
 *   </ProtectedRoute>
 * } />
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin, requireAdminOrModerator }) => {
  const { isAuthenticated, isAdmin, isAdminOrModerator, loading } = useAuth();
  const { hasUserProfile, loading: userProfileLoading } = useUserProfile();
  const location = useLocation();
  const { t } = useTranslation();

  // Mientras verifica autenticación, mostrar spinner
  if (loading || userProfileLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Spin size="large" />
        <p style={{ color: '#666', fontSize: 14 }}>{translate(t, TRANSLATIONS.LAYOUT_LOADING)}</p>
      </div>
    );
  }

  // Si no está autenticado, guardar la URL destino y redirigir a login
  if (!isAuthenticated) {
    // Guardar la URL que el usuario quería visitar (pathname + search params)
    const redirectUrl = location.pathname + location.search;
    localStorage.setItem('redirectAfterLogin', redirectUrl);
    
    // Agregar el parámetro redirect a la URL para que el usuario lo vea
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectUrl)}`} replace />;
  }

  // Rutas de admin: además de estar logueado, el AppRole del JWT debe ser "Admin"
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Panel de administración: el AppRole del JWT debe ser "Admin" o "Moderator"
  if (requireAdminOrModerator && !isAdminOrModerator) {
    return <Navigate to="/dashboard" replace />;
  }

  // Accounts on applications that require age verification must clear
  // /verify-age before reaching anywhere else in the app — see
  // RegistrationForm (sets this flag) and AgeVerification.tsx (clears it
  // once the backend confirms the account is verified).
  if (location.pathname !== '/verify-age' && localStorage.getItem(PENDING_AGE_VERIFICATION_KEY) === 'true') {
    return <Navigate to="/verify-age" replace />;
  }

  // Registration's step 2 for this app (username/display name/photo/phone/
  // country/language) -- see UserProfileProvider/CompleteProfile.tsx. Every
  // account needs one before reaching anywhere else in HeavyDutyMachinery. Also
  // exempts /verify-age: a brand-new account can need both steps at once,
  // and this check must not fight the age-verification redirect above --
  // otherwise the two Navigate calls bounce the user between the two
  // routes forever ("Maximum update depth exceeded").
  if (location.pathname !== '/complete-profile' && location.pathname !== '/verify-age' && !hasUserProfile) {
    return <Navigate to="/complete-profile" replace />;
  }

  // Si está autenticado, mostrar el componente
  return children;
};

export default ProtectedRoute;